/**
 * What the lyrics chat keeps above its current line: the whole conversation before it, to scroll back
 * through. At rest only the last few answers show (`lyricsHistory`, fading upwards); the rest is there,
 * unseen, so the list can be scrolled, and shows while the user is looking back.
 */
import type { ChatEntry } from '../../core/conversation';

export interface HistoryLine {
  entry: Extract<ChatEntry, { role: 'assistant' | 'user' }>;
  /** Shown at rest (one of the last `keep` answers); the others show only while scrolling back. */
  atRest: boolean;
  /** At rest, how faint it is (the older, the fainter: 0.25 up to 0.6); null when not shown at rest. */
  restOpacity: number | null;
}

/** The lines before `currentId` (the answer on the big line), oldest first; touches only with `touchPills`. */
export function historyLines(entries: ChatEntry[], currentId: string | undefined, keep: number, touchPills: boolean): HistoryLine[] {
  const end = currentId === undefined ? entries.length : entries.findIndex((e) => e.id === currentId);
  const before = entries.slice(0, end === -1 ? entries.length : end).filter(
    (e): e is HistoryLine['entry'] => e.role === 'assistant' || (e.role === 'user' && (touchPills || e.source !== 'touch')),
  );
  const answers = before.filter((e) => e.role === 'assistant');
  const shown = keep > 0 ? answers.slice(-keep).map((e) => e.id) : [];
  return before.map((entry) => {
    const rank = shown.indexOf(entry.id) + 1; // 1 = the oldest shown
    return { entry, atRest: rank > 0, restOpacity: rank > 0 ? 0.25 + (0.35 * rank) / shown.length : null };
  });
}
